import { employmentDocumentMessages, romeTimeMessages } from '@fide/shared';
import type { Messages } from '../lib/i18n';

export const en: Messages = {
  ...romeTimeMessages('en'),
  ...employmentDocumentMessages('en'),
  "org.title": "Organization",
  "org.explanation": "Units describe the organization and grant no access. The direct manager handles team requests. Extended scope must be assigned explicitly: it includes subordinate teams through active managers, without HR access to documents or contracts.",
  "org.units": "Departments and units",
  "org.newUnit": "New unit",
  "org.archived": "Archived",
  "org.noUnits": "No units configured.",
  "org.people": "People and reporting lines",
  "org.search": "Search name or employee number",
  "org.count": "People found: {n}",
  "org.manager": "Direct manager",
  "org.subtree": "Also manages subordinate teams",
  "org.direct": "Direct reports only",
  "org.unassigned": "Unassigned",
  "org.unitEditor": "Organizational unit",
  "org.name": "Unit name",
  "org.parent": "Parent unit",
  "org.root": "Company root",
  "org.archive": "Archive unit",
  "org.archiveHint": "Only a unit without people or child units can be archived. No data is deleted.",
  "org.clearManager": "Remove manager",
  "org.findManager": "Find an active manager",
  "org.accessWarning": "The chain changes who can view attendance, balances and requests and approve them. Each extended level requires an active member with an approving role. No self-approval. Emails still go to the direct manager and HR.",
  "org.confirm": "I confirm the displayed structure and access scope.",
  "org.invalid": "Invalid configuration: check role, status and unit.",
  "org.cycle": "The structure would create a cycle between units or managers.",
  "org.stale": "The structure changed. Close the editor, reload and try again.",
  "org.busy": "Another edit is in progress. Try again.",
  "org.inUse": "The unit contains people or child units. Reassign them before archiving.",
  "org.limit": "The limit of 500 units has been reached.",
  "wizard.identity": "Person and recipient",
  "wizard.employment": "Employment details",
  "wizard.documents": "Documents and agreements",
  "wizard.documentsHint": "Define what HR must verify after creation. The recipient remains the email address reviewed in the first step.",
  "wizard.employmentContractRequired": "The employment contract will be added to the checklist and can be linked to the employment version from the worker profile.",
  "wizard.individualAgreement": "A separate individual agreement is expected",
  "wizard.noSignatureClaim": "The checklist records administrative verification only: it does not prove signature, delivery, acceptance or legal validity.",
  "wizard.required": "Required",
  "wizard.notRequired": "Not expected",
  "wizard.review": "Review and invitation",
  "wizard.steps": "Onboarding steps",
  "wizard.next": "Continue",
  "wizard.previous": "Back",
  "wizard.create": "Create employee and send invitation",
  "wizard.confirm": "I checked the details, company and recipient. I confirm profile creation and sending the invitation.",
  "wizard.delivery": "The access invitation will be sent to {email}.",
  "wizard.scope": "Creating a profile does not sign a contract or certify hiring or CCNL applicability.",
  "wizard.unknown": "Not recorded",
  "wizard.fixRequired": "Complete or correct the details and retry loading references before confirming.",
  'employment.datesInvalid':'Check dates: record employment start before contract/probation end; start cannot follow the version effective date, the contract cannot already have expired and probation must fall within employment dates.',
  "employment.employment_start": "Employment start",
  "employment.contract_end": "Reported contract end",
  "employment.probation_end": "Reported probation end",
  "employment.datesHint": "Dates confirmed by HR: blank means not recorded. They do not calculate probation, renew contracts or terminate employment automatically.",
  'draft.title':'Onboarding to complete',
  'draft.hint':'Drafts are restricted to HR and owner and deleted after 30 days without saving. Saving never creates employees or sends mail.',
  'draft.empty':'No saved drafts.',
  'draft.unnamed':'Unnamed draft',
  'draft.completed':'Employee created: check invitation delivery in their profile.',
  'draft.delivery':'Invitation delivery',
  'draft.resume':'Resume',
  'draft.discard':'Remove draft',
  'draft.discardConfirm':'Remove this draft or reminder? An employee already created will not be deleted.',
  'draft.closeConfirm':'Close without saving? Use “Save draft and close” to resume later.',
  'draft.save':'Save draft and close',
  'draft.invalid':'Invalid draft. Check fields; do not enter attachments or medical details.',
  'draft.conflict':'Draft changed or onboarding already completed. Close and resume from the refreshed list. Check the employee before resending an invitation.',
  'draft.unavailable':'Draft expired or unavailable. Refresh the list.',
  'draft.limit':'Limit of 100 drafts reached. Remove unneeded drafts.',
  "calendarConfig.invalid": "Invalid date or type. Check the details.",
  "calendarConfig.stale": "This exception changed. Reload before saving.",

  "workCalendar.company": "Company exception",
  "workCalendar.site": "Site exception",
  "workCalendar.personal": "Personal exception",

  "calendarConfig.title": "Holidays and exceptions",
  "calendarConfig.hint": "They apply to the shift start date. A working day uses saved intervals, without inventing hours. No medical data fields.",
  "calendarConfig.empty": "No exceptions configured",
  "calendarConfig.scope": "Company or site",
  "calendarConfig.kind": "Type",
  "calendarConfig.company": "Entire company",
  "calendarConfig.personal": "Personal exception",
  "calendarConfig.patron": "Local holiday: non-working day",
  "calendarConfig.closure": "Closure: non-working day",
  "calendarConfig.workday": "Working day",
  "calendarConfig.confirm": "I confirm the date, scope and type",

  "workCalendar.title": "Planned calendar",
  "workCalendar.hint": "Projection from saved schedules and national holidays. It does not certify attendance or recalculate requests or balances.",
  "workCalendar.unknown": "Schedule or employment reference incomplete",
  "workCalendar.national": "National holiday",
  "workCalendar.minutes": "Planned wall-clock minutes",
  "workCalendar.previous": "Previous month",
  "workCalendar.next": "Next month",

  "schedule.title": "Weekly schedule",
  "schedule.hint": "Italian time. Breaks are gaps; punches, leave and balances are unchanged.",
  "schedule.empty": "Schedule not configured",
  "clockPolicy.title": "Clock tolerances",
  "clockPolicy.hint": "Values declared by HR for this schedule version and site; they are not inferred from the CCNL.",
  "clockPolicy.notConfigured": "No tolerances are configured for this schedule.",
  "clockPolicy.effectiveFrom": "Effective from",
  "clockPolicy.lateStart": "Tolerance after planned start",
  "clockPolicy.earlyEnd": "Tolerance before planned end",
  "clockPolicy.minutes": "minutes",
  "clockPolicy.scheduleVersion": "Schedule version",
  "clockPolicy.source.label": "Declared source",
  "clockPolicy.source.company_policy": "Company policy",
  "clockPolicy.source.employment_agreement": "Employment agreement",
  "clockPolicy.source.consultant_review": "Labour consultant review",
  "clockPolicy.noEffect": "This is recorded and displayed only. It does not change punches, hours, payroll or reports; HR must review the rule before using it.",
  "clockPolicy.confirm": "I confirm these declared values and their effective date.",
  "clockPolicy.addVersion": "Record a tolerance version",
  "clockPolicy.stale": "The policy changed in another session. Reload it.",
  "clockPolicy.scheduleMismatch": "The selected schedule version is not effective on this date.",
  "schedule.days": "Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday",
  "schedule.next": "Next day",
  "schedule.incompatible": "Employment data changed: schedule needs reconfirmation.",
  "schedule.minutes": "Weekly minutes",
  "schedule.day": "Day",
  "schedule.start": "Start",
  "schedule.end": "End",
  "schedule.add": "Add interval",
  "schedule.remove": "Remove interval",
  "schedule.confirm": "I confirm the intervals and effective date",
  "schedule.invalid": "Invalid intervals or weekly total.",

  "companyCcnl.title": "Company CCNL reference periods",
  "companyCcnl.hint": "Saving the first reference enables checks for new individual assignments: code and date must match this list. Existing history is unchanged. These are organisational dates, not a legal applicability determination.",
  "companyCcnl.active": "Checks enabled: new assignments must match an available company reference period.",
  "companyCcnl.closeEnd": "Assignment end date",
  "companyCcnl.initial": "Initial CCNL reference",
  "audit.table.company_ccnls": "Company CCNL references",
  "audit.field.company_ccnl_id": "Assigned CCNL reference",
  "audit.field.ccnl_policy_enabled": "Company CCNL reference checks",
  "audit.field.effective_from": "Effective date",
  "audit.field.effective_to": "End date",
  "audit.field.closed_at": "Closure date",
  "audit.field.voided_at": "Withdrawal date",
  "audit.field.code": "Code",
  "companyCcnl.empty": "List not configured: new records still use free references.",
  "companyCcnl.open": "No end date",
  "companyCcnl.end": "Assignment end date (optional)",
  "companyCcnl.close": "Close period",
  "companyCcnl.closeHint": "Closing is final and cannot precede today or future assignments. Existing relationships are preserved.",
  "companyCcnl.voidHint": "Withdraw only an unused reference. Checks remain enabled even when the list is empty.",
  "companyCcnl.invalid": "Select a company CCNL reference available on the effective date.",
  "companyCcnl.overlap": "Overlapping periods for the same code.",
  "companyCcnl.inUse": "Reference already withdrawn or used: it cannot be withdrawn.",
  "companyCcnl.closeInvalid": "Invalid end date or conflicting future assignments.",
  "employment.companyCcnlPeriod": "Company CCNL reference period: {from} – {to}",

  'worklists.updated': 'The data changed. List refreshed from the first page.',
  "company.identityHint": "The VAT number identifies this company and cannot be edited here. Optional fields are not activation requirements; the CCNL is a reference here, not an automatic accrual rule.",
  "company.edit": "Edit company details",
  "company.setup": "Initial setup",
  "company.setupHint": "These checks describe configured data; they do not certify service readiness. Also check the kiosk, invitations, permissions and opening a payslip on the device.",
  "company.registered": "Company registered",
  "company.sites": "Registered sites: {count}. Open to configure kiosks and clock-in methods.",
  "company.recipients": "Employees with active accounts and keys: {ready} / {total}. Excludes owners, HR and managers. Check invitations and devices in their profiles.",
  "company.invalid": "Check the format or length of this field.",
  "company.conflict": "Details changed or access is no longer available. Cancel, refresh the page and review before retrying.",
  "company.savedRefreshFailed": "Your changes were saved, but session data did not refresh. Retry the read; do not save again.",
  "register.created": "Company registered",
  "register.createdReady": "Loading your company access.",
  "register.createdRefreshFailed": "The company was registered, but access did not load. Retry the read without registering it again.",

  "invitations.title": "Invitations",
  "invitations.historyHint": "Link status, not proof of email delivery. Tokens are not stored in plaintext: sharing again requires a new link, invalidating the previous one.",
  "invitations.status.pending": "Valid",
  "invitations.status.expired": "Expired",
  "invitations.status.redeemed": "Accepted",
  "invitations.status.revoked": "Revoked",
  "invitations.created": "Created",
  "invitations.expires": "Expires",
  "invitations.redeemed": "Accepted on",
  "invitations.revoke": "Revoke link",
  "invitations.confirmRevoke": "Revoke this link? It can no longer be used to activate the account.",
  "invitations.empty": "No invitations recorded.",
  'directory.filters': 'Employee filters',
  'directory.search': 'Name, employee number, email or tax code',
  'directory.unassigned': 'No assigned site',
  'directory.sort': 'Sort by',
  'directory.sort.name-asc': 'Name · A–Z',
  'directory.sort.name-desc': 'Name · Z–A',
  'directory.sort.number-asc': 'Employee number · ascending',
  'directory.sort.number-desc': 'Employee number · descending',
  'directory.results': 'Filtered employees: {filtered} / {total}. Site, role and status are their current values.',
  'directory.unavailable': 'The list and counts are unavailable until the data loads successfully.',
  'directory.noResults': 'No employees match these filters. Reset the filters to see the full list.',
  'directory.pagination': 'Employee pages',
  'directory.tableScroll': 'Employee table with horizontal scrolling',
  "employees.myProfile": "My profile",
  "member.tab.employment": "Employment details",
  "employment.reload": "Reload",
  "employment.saved": "Version saved.",
  "employment.new": "New version / replacement",
  "employment.copy": "Use as a starting point",
  "employment.withdraw": "Withdraw future version",
  "employment.confirmWithdraw": "Withdraw this future version? Its record remains visible.",
  "employment.save": "Save version",
  "employment.stale": "The data changed. Reload before saving.",
  "employment.dateInvalid": "The date cannot precede the latest version or change the past.",
  "employment.notFuture": "Only a still-valid future version can be withdrawn.",
  "employment.title": "Employment details",
  "employment.hint": "References entered by HR: not a signed contract and do not activate balance accrual. Job titles do not grant access permissions.",
  "employment.job_title": "Job title",
  "employment.category": "Category",
  "employment.level": "Level",
  "employment.contract_type": "Contract type",
  "employment.ccnl_reference": "CCNL reference",
  "employment.weekly_hours": "Weekly hours",
  "employment.effective_from": "Effective from",
  "employment.empty": "HR has not entered employment details yet.",
  "ccnl.search": "Search by code, name, or sector",
  "ccnl.snapshot": "Local catalogue copy generated on {date}.",
  "ccnl.stale": "The catalogue copy needs an update review (operational threshold: 14 days). Saved references are preserved; confirm the text and renewals with your labour adviser.",
  "ccnl.effectiveFrom": "Effective date reported by CNEL",
  "ccnl.expiry": "Contract expiry reported by CNEL",
  "ccnl.classification": "CNEL classification status",
  "ccnl.agreement": "Agreement type",
  "ccnl.received": "CNEL receipt date",
  "ccnl.datesHint": "These values describe the catalogue record. A past expiry or classification status alone does not determine whether the CCNL applies. Confirm the text and renewals with your labour adviser.",
  "ccnl.missing": "The saved reference is absent from this catalogue copy. It is preserved; check the code with your adviser before replacing it.",
  "ccnl.selected": "CNEL collective agreement",
  "ccnl.none": "Select a reference",
  "ccnl.unknown": "Not confirmed by the labour consultant",
  "ccnl.legacy": "Previous reference: {value}",
  "ccnl.unavailable": "The CNEL catalog is unavailable. Try again when the connection is restored.",
  "ccnl.hint": "Source: CNEL Current Archive. This selection is a reference for the labour consultant to confirm; it does not determine applicability or calculate balances automatically.",
  "employment.onboardingHint": "Enter employment details before inviting the employee. These are operational references, not a signed contract, and do not enable automatic schedule or leave calculations.",
  "employment.current": "Current",
  "employment.past": "Historical",
  "employment.future": "Scheduled",
  "employment.voided": "Voided / replaced",
  'app.tagline': 'Company portal',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.company': 'Company',
  'common.language': 'Language',
  'common.loading': 'Loading…',
  'common.edit': 'Edit',
  'common.add': 'Add',
  'common.copy': 'Copy',
  'common.copied': 'Copied',
  'common.retry': 'Try again',
  'common.logout': 'Sign out',
  'common.actions': 'Actions',
  'common.none': 'None',
  'common.optional': 'optional',

  'nav.presence': 'Attendance',
  'nav.cartellino': 'Timesheets',
  'nav.employees': 'Employees',
  'nav.sites': 'Sites and kiosks',
  'nav.payroll': 'Documents',
  'nav.compensation': 'Compensation',
  'nav.requests': 'Requests',
  'nav.communications': 'Communications',
  'communications.eyebrow': 'COMPANY NOTICEBOARD',
  'communications.title': 'Communications',
  'communications.hint': 'Plain-text updates visible to active employees. No attachments or read receipts.',
  'communications.compose': 'New update',
  'communications.subject': 'Title',
  'communications.body': 'Message',
  'communications.expiry': 'Expiry (optional)',
  'communications.scope': 'Visible to all active employees in the company.',
  'communications.publish': 'Publish update',
  'communications.published': 'Update published.',
  'communications.error': 'The operation could not be completed. Please try again.',
  'communications.archiveTitle': 'Published updates',
  'communications.archive': 'Archive',
  'communications.publishedAt': 'Published {date}',
  'communications.expiresAt': 'Expires {date}',
  'communications.empty': 'There are no active updates.',
  'communications.results': 'Updates {from}–{to} of {total}',
  'communications.pagination': 'Update pages',
  'communications.archiveConfirmTitle': 'Archive this update?',
  'communications.archiveConfirmBody': 'The update “{title}” will no longer be visible to employees. Confirm?',
  'common.skipToContent': 'Skip to content',
  'nav.audit': 'Activity log',
  'nav.settings': 'Settings',

  'config.missingTitle': 'Portal not configured',
  'config.missingBody':
    'Set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY in apps/web-portal/.env (see .env.example) and restart.',

  'login.title': 'Sign in to the portal',
  'login.subtitle': 'No passwords: a code by e-mail or this device’s passkey.',
  'login.email': 'Work e-mail',
  'login.sendCode': 'Send me the code',
  'login.codeSent': 'We sent a code to {email}.',
  'login.linkHint': 'If the e-mail has a link instead of a code, open it in this same browser.',
  'login.code': '6-digit code',
  'login.verify': 'Sign in',
  'login.passkey': 'Sign in with a passkey',
  'login.or': 'or',
  'login.changeEmail': 'Use another address',

  'access.noCompanyTitle': 'No company linked to this account',
  'access.noCompanyBody': 'If you own or run HR for a company, register it. If you are an employee, use your invitation and the Fide app.',
  'access.registerCompany': 'Register your company',
  'access.employeeTitle': 'This portal is for HR and owners',
  'access.employeeBody': 'To clock in, read your payslips and request leave, use the Fide app on your phone.',

  'register.title': 'Register your company',
  'register.legalName': 'Legal name',
  'register.vat': 'VAT number (partita IVA)',
  'register.vatInvalid': 'Invalid VAT number (11 digits with a check digit).',
  'register.ownerName': 'Your full name',
  'register.fiscalCode': 'Company tax code',
  'register.pec': 'PEC',
  'register.sdi': 'SDI code',
  'register.inps': 'INPS number',
  'register.ccnl': 'Collective agreement (CCNL)',
  'register.address': 'Registered office',
  'register.submit': 'Register company',
  'register.note': 'You will be the owner of the company account and can add HR and employees right after.',

  'presence.title': 'Attendance',
  'presence.live': 'Live',
  'presence.inNow': 'In now',
  'presence.today': 'Today’s punches',
  'presence.empty': 'No punches yet today.',
  'presence.in': 'In',
  'presence.out': 'Out',
  'presence.receipt': 'Receipt',
  'presence.employee': 'Employee',
  'presence.time': 'Time',
  'presence.method': 'Method',
  'presence.flags': 'Flags',

  'report.title': 'Monthly report for the payroll consultant',
  'report.body': 'Entries, exits, hours worked, approved absences and anomalies per employee and day, in Italian time. It feeds payroll and the LUL; it does not replace them. The file is built in this browser.',
  'report.scopeNotice': 'DRAFT FOR THE LABOUR CONSULTANT TO REVIEW. It sums intervals between punches and approved absences; it does not know contractual schedules, breaks, site holidays or CCNL rules. It does not calculate payroll or overtime and is not a validated import file. Reconcile it before use.',
  'report.month': 'Month',
  'report.prepare': 'Prepare the report',
  'report.loading': 'Preparing…',
  'report.empty': 'No punches or absences this month.',
  'report.ready': '{employees} employees · {lines} days',
  'report.anomalies': '{n} anomalies to check before sending the file (see the “Anomalies” column).',
  'report.detail': 'Download daily detail (CSV)',
  'report.summary': 'Download per-employee summary (CSV)',
  'report.dayUnit': 'd',
  'report.allocationRequired': 'Export blocked: {employee}, {code}, {from} to {to}, approved quantity {quantity}. Daily allocation cannot be determined with the current calendar (Mon–Fri, excluding national holidays). Agree the allocation with the payroll adviser; do not change the historical request to fit the calendar.',
  'report.col.number': 'Employee number',
  'report.col.cf': 'Codice fiscale',
  'report.col.employee': 'Employee',
  'report.col.date': 'Date',
  'report.col.intervals': 'Entries-Exits',
  'report.col.hours': 'Hours (hh:mm)',
  'report.col.hundredths': 'Hours (decimal)',
  'report.col.absences': 'Absences',
  'report.col.anomalies': 'Anomalies',
  'report.col.days': 'Days worked',
  'report.col.anomalyCount': 'Anomalies',
  'report.anomaly.missing_out': 'missing exit',
  'report.anomaly.missing_in': 'missing entry',
  'report.anomaly.long_shift': 'shift over 16 hours',
  'report.anomaly.corrected': 'corrected by HR',
  'report.anomaly.flagged': 'punch flagged',
  'method.geo': 'Location',
  'method.qr': 'Kiosk QR',
  'method.manual': 'Correction',
  'flag.late_sync': 'Synced late',
  'flag.clock_skew': 'Phone clock out of sync',
  'flag.sequence': 'Two punches of the same type in a row',
  'flag.outside_geofence': 'Outside the site',
  'flag.mock_location': 'Simulated location',
  'flag.manual_correction': 'Approved correction',
  'flag.hr_entry': 'Recorded by HR',
  'flag.unclosed_break': 'Clock-out with unclosed break',

  'employees.title': 'Employees',
  'employees.add': 'New employee',
  'employees.onboardingInviteSent': 'Employee created and invitation accepted by the mail service. This does not confirm delivery to the worker’s inbox.',
  'employees.onboardingInviteFailed': 'The employee was created, but invitation sending could not be confirmed.',
  'employees.onboardingRetryHint': 'Retrying creates a new invitation and invalidates any previous link. If the first email arrived, that link will stop working.',
  'employees.onboardingRetry': 'Issue and send another invitation',
  'employees.onboardingFinish': 'Finish',
  'employees.onboardingProfile': 'Open employee profile',
  'employees.onboardingNextSteps': 'From the profile, review employment documents and complete the checklist. You can save an HR-only encrypted compensation draft; publish it to the employee after verifying their registered device.',
  'employees.fullName': 'Full name',
  'employees.email': 'E-mail',
  'employees.cf': 'Tax code (codice fiscale)',
  'employees.cfInvalid': 'Invalid codice fiscale.',
  'employees.number': 'Employee number',
  'employees.site': 'Site',
  'employees.role': 'Role',
  'employees.status': 'Status',
  'employees.device': 'Device',
  'employees.deviceRegistered': 'Enrolled',
  'employees.actions': 'Actions',
  'employees.noDevice': 'No device',
  'employees.invite': 'Invite',
  'employees.inviteTitle': 'Invitation for {name}',
  'employees.inviteBody':
    'We will send the link to {email}. It is valid for 7 days and only works with that address. You can also share it by hand (WhatsApp, SMS, printed QR).',
  'employees.inviteSend': 'Send invitation by e-mail',
  'employees.inviteLinkOnly': 'Link only, to share by hand',
  'employees.inviteEmail': 'Send by e-mail',
  'employees.inviteEmailed': 'E-mail sent to {email}. The link above is the same one.',
  'employees.inviteEmailFailed': 'The e-mail did not go out. Share the link by hand or try again in a moment.',
  'employees.inviteNewLinkHint': 'Creates a new link: the previous one stops working.',
  'employees.inviteNoEmail': 'This employee has no e-mail address: without one they cannot activate the account. Add it to their details.',
  'employees.empty': 'No employees yet. Add the first one.',

  'import.button': 'Import CSV',
  'import.title': 'Import employees from CSV',
  'import.body': 'Export the staff list from your payroll software or Excel as CSV. Recognised columns: full name (or first and last name), e-mail, codice fiscale, employee number, site. The file is read in this browser and nothing is saved until you confirm.',
  'import.template': 'Download the template',
  'import.file': 'CSV file',
  'import.missingName': 'The file has no name column: it needs “Full name”, or “First name” and “Last name”.',
  'import.empty': 'The file has no rows to import.',
  'import.summary': '{ready} to import · {exists} already there · {errors} to fix',
  'import.line': 'Line',
  'import.status.ready': 'To import',
  'import.status.exists': 'Already there',
  'import.status.error': 'To fix',
  'import.existing': 'already registered as {name}',
  'import.noEmail': 'no e-mail: cannot be invited',
  'import.issue.name_missing': 'name missing',
  'import.issue.name_too_long': 'name too long',
  'import.issue.email_invalid': 'invalid e-mail',
  'import.issue.cf_invalid': 'invalid codice fiscale',
  'import.issue.number_too_long': 'employee number too long',
  'import.issue.site_unknown': 'site not found',
  'import.issue.duplicate_in_file': 'repeats line {line}',
  'import.skipped': 'Lines to fix will not be imported: fix them and import the file again.',
  'import.tooMany': 'At most {max} employees per file: split it.',
  'import.submit': 'Import {n} employees',
  'import.done': 'Imported {n} employees. You can now invite them from the list.',
  'import.rowFailed': 'Line {line}: {reason}. No employee was imported.',
  'import.reason.email_taken': 'e-mail already used by another employee',
  'import.reason.cf_taken': 'codice fiscale already in the company',
  'import.reason.invalid': 'invalid value',
  'import.reason.site': 'invalid site',
  'import.reason.other': 'unexpected error',
  "roles.guideTitle": "Roles and managers: who can do what",
  "roles.guideBody": "In Manage → Details, choose the access role and direct manager. In Manage → Employment details, record job title, category, level and CCNL with an effective date: these details do not grant permissions. In Organization, configure departments, child units, reporting chains and temporary delegations. Each person has one approver directly above them. Access to subordinate teams requires explicit extended scope; it does not enable HR documents or employment details.",
  "roles.scope.employee": "Uses the app for their own details, punches, requests and documents. No access to the company portal.",
  "roles.scope.manager": "Accesses presence, reports and requests for the assigned team. Can approve team requests, never their own. Cannot access HR pages for employees, payslips, sites or GDPR requests.",
  "roles.scope.hr_admin": "Manages company employees, presence, requests, encrypted document deliveries, sites and GDPR requests. Can appoint managers; only an owner changes HR and Owner roles.",
  "roles.scope.company_owner": "Has HR functions and can grant or remove HR and Owner roles. At least one active owner must remain.",
  "roles.confirmTitle": "Change {name}’s role: {from} → {to}?",
  "roles.confirmBody": "This changes access permissions. It is saved separately from the other form details. Permissions also require an active membership.",
  "roles.releasesTeam": "{n} people will lose this manager: {names}. You will need to assign another manager; HR can still handle their requests.",
  "roles.selfChange": "You are changing your own role: you may lose access to parts of the portal.",
  "roles.confirm": "Confirm role change",
  'role.employee': 'Employee',
  'role.manager': 'Manager',
  'role.hr_admin': 'HR',
  'role.company_owner': 'Owner',
  'status.invited': 'Invited',
  'status.active': 'Active',
  'status.suspended': 'Suspended',
  'status.erased': 'Erased',

  'sites.title': 'Sites and kiosks',
  'sites.add': 'New site',
  'sites.empty': 'No sites are configured. Add a site to get started.',
  'sites.noKiosks': 'No kiosks are configured for this site.',
  'sites.retrySites': 'Retry loading sites',
  'sites.retryKiosks': 'Retry loading kiosks',
  'sites.name': 'Name',
  'sites.address': 'Address',
  'sites.latitude': 'Latitude',
  'sites.longitude': 'Longitude',
  'sites.radius': 'Radius (m)',
  'sites.geo': 'Location-based punching',
  'sites.geoWarning':
    'Enable it only after the union agreement or the Labour Inspectorate authorisation (art. 4 L. 300/1970). Until then people punch with the kiosk QR.',
  'sites.qrOnly': 'QR only',
  'sites.geoOn': 'Location + QR',
  'sites.kiosks': 'Kiosks',
  'sites.addKiosk': 'New kiosk',
  'sites.kioskName': 'Kiosk name (e.g. Entrance)',
  'sites.pairingTitle': 'Pair the tablet',
  'sites.pairingBody': 'On the entrance tablet open {url} and enter this code within 10 minutes:',
  'sites.revokeKiosk': 'Disable',
  'sites.revokeTitle': 'Disable kiosk {name}?',
  'sites.revokeWarning': 'Kiosk {name} will no longer record QR clock-ins. To use this tablet again, you will need to pair a new kiosk.',
  'sites.pairingMissing': 'The kiosk was created, but no pairing code was returned. Do not retry: contact support.',
  'kioskStatus.pairing': 'To pair',
  'kioskStatus.active': 'Active',
  'kioskStatus.revoked': 'Disabled',

  'kiosk.pairTitle': 'Pair this kiosk',
  'kiosk.pairBody': 'Enter the code shown in the portal (Sites and kiosks → New kiosk).',
  'kiosk.code': 'Pairing code',
  'kiosk.pair': 'Pair',
  'kiosk.scan': 'Scan the code with the Fide app to clock in',
  'kiosk.refresh': 'New code in {s} s',
  'kiosk.unpair': 'Unpair kiosk',
  'kiosk.clockWarning': 'This device’s clock is off by {s} s: fix it, otherwise punches will be rejected.',

  'payroll.title': 'Employee documents',
  'comp.title': 'Compensation references',
  'comp.intro': 'Encrypted in the browser and readable on an authorized HR phone and the employee’s phone. Fide holds no recovery key.',
  'comp.employee': 'Employee', 'comp.choose': 'Select an employee', 'comp.label': 'Reference description',
  'comp.amount': 'Amount', 'comp.currency': 'Currency', 'comp.frequency': 'Frequency', 'comp.frequency.hourly': 'Hourly',
  'comp.frequency.monthly': 'Monthly', 'comp.frequency.annual': 'Annual', 'comp.frequency.one_time': 'One-time',
  'comp.effective': 'Effective from', 'comp.replaces': 'Corrects reference', 'comp.new': 'New reference',
  'comp.save': 'Encrypt and save', 'comp.saving': 'Encrypting and saving…', 'comp.saved': 'Reference saved encrypted.',
  'comp.empty': 'No references recorded.', 'comp.history': 'Encrypted history', 'comp.privacy': 'Plaintext amounts and descriptions are not sent to the database; only encrypted content is stored. References are not payslips and do not calculate or grant entitlements automatically.',
  'comp.keys': 'Authorized phone keys', 'comp.verify': 'I compared this code with the named Fide phone.',
  'comp.keyMismatch': 'The fingerprint does not match the public keys. Saving is blocked.',
  'comp.keyChanged': 'The phone key changed: compare the new code on the phone before continuing.',
  'comp.noKey': 'An active employee or current HR phone key is missing.', 'comp.error': 'The operation could not be completed. Check the keys and connection.',
  'comp.conflict': 'This reference was already corrected by another session. The history was refreshed; select the current version and try again.',
  'comp.saveDraft': 'Save encrypted draft', 'comp.draftSaved': 'Encrypted draft saved for HR phones.',
  'comp.draftHint': 'A draft is encrypted only for the verified HR phones. The employee cannot see it until an HR phone publishes it to their active key.',
  'comp.drafts': 'Encrypted drafts', 'comp.draftsEmpty': 'No pending drafts.',
  'comp.draftExpires': 'Expires {date}', 'comp.draftsPublish': 'Open Compensation references on an authorized HR phone to review and publish a draft.',
  'comp.discardDraft': 'Discard draft', 'comp.discardDraftConfirm': 'Permanently discard this encrypted draft?', 'comp.draftDiscarded': 'Encrypted draft discarded.',
  'comp.read': 'Request portal access', 'comp.selectRead': 'Select references to read', 'comp.qrTitle': 'Scan with an authorized HR phone',
  'comp.qrHint': 'Open Compensation references in the Fide HR app and scan this code. Compare the security code also shown on the phone.',
  'comp.fingerprint': 'Verification code: {code}', 'comp.waiting': 'Waiting for approval from the HR phone…',
  'comp.cancel': 'Cancel request', 'comp.readDone': 'References opened after mobile approval. Close this view to clear the temporary copy.',
  'comp.expired': 'The request expired. Start a new one.', 'comp.readError': 'The phone cannot open these references or the request expired.',
  'comp.close': 'Close and clear',
  'comp.maxRead': 'You can request access to up to 25 references at a time.',
  'comp.maxRecipients': 'Up to 100 authorized phones can receive a reference.',
  'payroll.privacy':
    'The PDF is split and encrypted in this browser. Fide only receives encrypted files that only the employee’s phone can open.',
  'payroll.kind': 'Type',
  'kind.cedolino': 'Payslip',
  'kind.contract': 'Employment contract / hiring letter',
  'kind.contract_change': 'Contract change',
  'kind.training_certificate': 'Training certificate',
  'kind.hr_notice': 'HR communication',
  'kind.tfr': 'TFR / final settlement',
  'kind.cu': 'Certificazione Unica',
  'kind.other': 'Other document',
  'payroll.period': 'Month',
  'payroll.reference.month': 'Payslip month',
  'payroll.reference.taxYear': 'Tax year covered',
  'payroll.reference.date': 'Reference date',
  'payroll.docTitle': 'Title',
  'payroll.file': 'Bulk PDF (Zucchetti, TeamSystem…)',
  'payroll.fileDocument': 'Document PDF (may contain pages for multiple employees)',
  'payroll.analyze': 'Analyse',
  'payroll.analyzing': 'Reading the PDF…',
  'payroll.pages': '{n} pages',
  'payroll.matched': 'Ready to send',
  'payroll.issues': 'To check',
  'payroll.noKey': 'No active device: cannot receive encrypted documents yet.',
  'payroll.issue.unassigned': 'Page {page}: no employee recognised.',
  'payroll.issue.ambiguous': 'Page {page}: contains several employees.',
  'payroll.issue.identity_conflict': 'Page {page}: the tax code and name point to different employees.',
  'payroll.issue.unknown_employee': 'Page {page}: tax code not found in the company.',
  'payroll.publish': 'Encrypt and publish {n} documents',
  'payroll.encrypting': 'Encrypting {done}/{total}…',
  'payroll.uploading': 'Uploading {done}/{total}…',
  'payroll.publishing': 'Publishing…',
  'payroll.done': 'Published {n} documents: employees find them in the Fide app, under Documents.',
  'payroll.new': 'New distribution',
  'payroll.recipients': 'Recipients',
  'payroll.pagesCol': 'Pages',
  'payroll.continuation': 'pages without a codice fiscale added to the previous employee: {pages}',
  'payroll.manual': 'assigned by hand: {pages}',
  'payroll.state.ready': 'Ready',
  'payroll.state.reissue': 'Reissue: new phone',
  'payroll.state.delivered': 'Already delivered',
  'payroll.state.no_key': 'No active device',
  'payroll.state.key_changed': 'Device changed',
  'payroll.state.key_mismatch': 'Inconsistent security code: do not send',
  'payroll.firstUse': 'first time',
  'payroll.firstUseHint': 'First document encrypted to this phone from this browser. You can compare the security code with the employee’s app (My data → This phone).',
  'payroll.sendAgain': 'Send again (replaces it)',
  'payroll.verify': 'Verify',
  'payroll.assign': 'Assign to',
  'payroll.dontSend': '— Do not send —',
  'payroll.skippedPages': '{n} pages will not be sent.',
  'payroll.verifyTitle': 'Verify {name}’s phone',
  'payroll.verifyBody': 'This employee’s phone has changed. Ask them to open Fide → My data → This phone and read you the security code. Confirm only if it matches the current one; otherwise do not send and contact Fide support.',
  'payroll.verifyPrevious': 'Previous security code',
  'payroll.verifyCurrent': 'Current security code',
  'payroll.verifyConfirm': 'The code matches',
  'payroll.trustUnavailable': 'This browser cannot keep verified security codes (private browsing?): every phone will be treated as new.',
  'payroll.encryptedPdf': 'The PDF is password-protected. Export it without protection from the payroll software.',
  'payroll.notPdf': 'The file is not a readable PDF.',
  'payroll.cryptoBroken': 'This browser fails the cryptographic self-test: update Chrome, Edge, Firefox or Safari.',
  'payroll.retry': 'Try again',
  'payroll.reset': 'New PDF',
  'payroll.nothingToSend': 'Nothing to send.',
  'payroll.history': 'Distribution history',
  'payroll.historyEmpty': 'No distributions yet.',
  'payroll.historyRange': 'Distributions {from}–{to} of {total}',
  'payroll.historyPagination': 'Distribution pages',
  'payroll.documentsRange': 'Documents {from}–{to} of {total}',
  'payroll.documentsPagination': 'Document pages',
  'payroll.noDocuments': 'No documents in this distribution.',
  'payroll.opened': '{opened}/{total} opened',
  'payroll.draft': 'Not published',

  'requests.title': 'Requests',
  'requests.leave': 'Leave and permits',
  'requests.corrections': 'Missed punches',
  'requests.overtime': 'Overtime and Time Bank',
  'requests.smartWorking': 'Smart working',
  'requests.compensationType': 'Compensation type',
  'requests.compensation.payout': 'Payout on payslip',
  'requests.compensation.banca_ore': 'Time Bank (Compensatory rest)',
  'requests.empty': 'No pending requests.',
  'requests.approve': 'Approve',
  'requests.reject': 'Reject',
  'requests.verifyProtocol': 'Check PUC',
  'requests.verifyProtocolTitle': 'Check {name}’s protocol',
  'requests.verifyProtocolHint': 'Check this PUC in the INPS employer service. Fide records your confirmation but does not query INPS.',
  'requests.verifyProtocolConfirm': 'I confirm that I checked the PUC in the INPS service.',
  'requests.sickAwaitingHr': 'Awaiting HR check',
  'requests.sickVerified': 'PUC checked by HR',
  'requests.sickLegacy': 'No PUC check recorded in Fide',
  'requests.amendSick': 'Correct/extend',
  'requests.amendSickTitle': 'Correct {name}’s sick leave',
  'requests.sickChangeKind': 'Action',
  'requests.sickExtension': 'Extension',
  'requests.sickCorrection': 'Correction',
  'requests.periodFrom': 'From',
  'requests.periodTo': 'To',
  'requests.amendReason': 'Reason for change',
  'requests.amendSickHint': 'The original request stays in history. The new period returns to pending until HR checks its PUC.',
  'requests.period': 'Period',
  'requests.quantity': 'Amount',
  'requests.reason': 'Reason',

  'audit.title': 'Activity log',
  'audit.when': 'When',
  'audit.what': 'What',
  'audit.actor': 'Who',
  'audit.subject': 'Employee',
  'audit.actorUnknown': 'User no longer available',
  'audit.actorSystem': 'System',
  'audit.empty': 'No activity recorded.',
  'audit.note': 'Administrative and security log for the last 90 days: who acted and which fields changed, without personal values. Language changes are not logged.',
  'audit.pagination': 'Activity log pagination',
  'audit.results': 'Events {from}–{to} of {total}',
  'audit.table.companies': 'Company details',
  'audit.table.sites': 'Workplaces',
  'audit.table.members': 'Employee records and roles',
  'audit.table.member_identities': 'Employee identities',
  'audit.table.invitations': 'Invitations',
  'audit.table.kiosk_devices': 'Kiosks',
  'audit.table.device_keys': 'Employee devices',
  'audit.table.punch_corrections': 'Time record corrections',
  'audit.table.payroll_batches': 'Document deliveries',
  'audit.table.documents': 'Personnel documents',
  'audit.table.leave_types': 'Absence types',
  'audit.table.leave_balances': 'Leave balances',
  'audit.table.leave_requests': 'Absence requests',
  'audit.table.gdpr_requests': 'Privacy requests',
  'audit.table.company_announcements': 'Company notices',
  'audit.table.employment_terms': 'Employment terms',
  'audit.table.other': 'Other company record',
  'audit.field.status': 'status',
  'audit.field.revoked_at': 'revocation date',
  'audit.field.preferred_language': 'preferred language',
  'audit.field.geo_enabled': 'geolocation enabled',
  'audit.field.latitude': 'workplace latitude',
  'audit.field.longitude': 'workplace longitude',
  'audit.field.manager_member_id': 'assigned manager',
  'audit.field.site_id': 'assigned workplace',
  'audit.field.first_opened_at': 'first document opening',
  'audit.field.published_at': 'publication date',
  'audit.field.decided_at': 'decision date',
  'audit.field.decided_by': 'decision maker',
  'audit.field.auth_user_id': 'linked account',
  'audit.field.other': 'other changed fields',
  'op.INSERT': 'created',
  'op.UPDATE': 'changed',
  'op.DELETE': 'deleted',

  'settings.title': 'Settings',
  'settings.company': 'Company details',
  'settings.passkeys': 'Passkeys',
  'settings.passkeysBody':
    'A passkey lets you sign in with this device’s unlock (PIN, password or the method you already use), without e-mail codes. The device does the check: Fide receives nothing.',
  'settings.addPasskey': 'Register a passkey on this device',
  'settings.noPasskeys': 'No passkeys registered.',
  'settings.passkeyAdded': 'Passkey registered.',
  'settings.saved': 'Saved.',

  'error.signupNotAllowed': 'During the pilot, registration is by invitation. Contact us to enable your company.',
  'error.emailNotConfirmed': 'Confirm your e-mail address first.',
  'error.forbidden': 'You are not allowed to do this.',
  'error.notAuthenticated': 'Session expired: sign in again.',
  'error.memberNotInvitable': 'The employee has already activated their account.',
  'error.memberEmailMissing': 'Add the employee’s e-mail first.',
  'error.lastOwner': 'The company needs at least one owner.',
  'error.deviceKeyNotActive': 'An employee changed phone in the meantime: analyse the PDF again.',
  'error.uploadIncomplete': 'Some files were not uploaded: try again.',
  'error.supersedesInvalid': 'The document to replace is no longer valid: analyse the PDF again.',
  'error.notDraft': 'This distribution has already been published.',
  'error.pairingCodeInvalid': 'Invalid or expired code.',
  'error.notPending': 'Request already handled.',
  'error.duplicate': 'Already exists.',
  'error.invalidValue': 'One of the values is not valid.',
  'error.generic': 'Something went wrong. Try again.',
  'error.idempotencyConflict': 'The onboarding details changed during the retry. Review the employee record before submitting again.',

  // Block 1: lifecycle, HR entries, withdrawal, GDPR inbox
  'error.terminationDateRequired': 'Enter the last working day.',
  'error.terminationDateInvalid': 'The termination date cannot be in the future or more than a year ago: record it from the last working day.',
  'error.emailLocked': 'The e-mail is the employee’s login: it can only be corrected until they activate the account.',
  'error.managerInvalid': 'Pick another active person of the same company as manager.',
  'error.memberErased': 'This person’s data has been erased.',
  'error.selfEntry': 'You cannot record entries for yourself: another HR person or the owner must do it.',
  'error.memberNotActive': 'This person is not active on that date.',
  'error.reasonRequired': 'Write the reason (at least 3 characters).',
  'error.tsInFuture': 'A future time cannot be recorded.',
  'error.protocolRequired': 'Sick leave needs the protocol number of the INPS certificate.',
  'error.leaveTypeInactive': 'This absence type is not active.',
  'error.alreadyWithdrawn': 'The document has already been withdrawn.',
  'error.answerRequired': 'Write the answer for the employee.',
  'error.alreadyResolved': 'Request already closed.',
  'error.alreadyExtended': 'The extension can only be used once.',
  'error.deadlinePassed': 'The first month has passed: it can no longer be extended, answer now.',
  'error.memberNotTerminated': 'This erasure is only possible for former employees.',
  'nav.privacy': 'Privacy (GDPR)',
  'status.terminated': 'Terminated',
  'employees.manage': 'Manage',
  'member.tab.data': 'Details',
  'member.tab.status': 'Status and phone',
  'member.tab.entries': 'Record on their behalf',
  'member.emailInvitedHint': 'You can correct it until they activate the account: invitations already sent stop working.',
  'member.emailLockedHint': 'It is the address they sign in with: it cannot be changed here.',
  'member.cfChangeHint': 'The codice fiscale decides which payslip pages they receive: double-check it.',
  'member.manager': 'Request approver',
  'member.language': 'App language',
  'member.saved': 'Details saved.',
  'member.statusTitle': 'Status',
  'member.terminatedInfo': 'Last day: {day}. They can see their documents until {until}.',
  'member.suspendedSince': 'Suspended since {when}.',
  'member.selfNote': 'You cannot change your own status or record entries for yourself.',
  'member.invitedNote': 'They have not activated the account yet: for now you can only invite them or correct their details.',
  'member.suspend': 'Suspend',
  'member.reactivate': 'Reactivate',
  'member.undoTermination': 'Undo the termination',
  'member.terminate': 'Record the termination',
  'member.suspendExplain': 'Blocks all access at once: they cannot punch, see documents or file requests. It can be undone at any time. Punches made before, still queued on the phone, remain valid.',
  'member.terminateExplain': 'From now on they cannot punch or file requests; leave requested after the last day is cancelled. For 12 months they can still see and download their documents, and you can send the final payslip, the TFR and the CU.',
  'member.reactivateExplain': 'They get full access back: punches, requests and documents.',
  'member.suspendConfirm': 'Suspend access',
  'member.terminateConfirm': 'Record the termination',
  'member.reactivateConfirm': 'Reactivate',
  'member.lastDay': 'Last working day',
  'member.lastDayHint': 'They will see their documents until {until}.',
  'member.statusDone': 'Status updated: {status}.',
  'member.cancelledRequests': '{n} absence requests after the last day were cancelled.',
  'member.reportsWarning': '{n} people still have this person as manager: assign them a new one in their details.',
  'member.phoneTitle': 'Phone',
  'member.phoneSince': 'active since {when}',
  'member.revokePhone': 'Revoke phone',
  'member.phoneRevoked': 'Phone revoked. Next time the app will ask to set up the phone again.',
  'member.revokeExplain': 'Use it if the phone is lost, stolen or replaced. From now on that phone cannot punch or open new documents. The employee sets up the app on the new phone and payslips already sent must be re-sent (the Payslips page tells you).',
  'member.revokeReason': 'Reason',
  'member.revokeReason.lost': 'Lost',
  'member.revokeReason.stolen': 'Stolen',
  'member.revokeReason.replaced': 'Replaced with another phone',
  'member.revokeReason.other': 'Other',
  'member.revokeConfirm': 'Revoke this phone',
  'member.entriesExplain': 'For people who don’t use their own phone, or to correct a day. The employee’s app shows “recorded by HR” with the reason, and they can contest it.',
  'member.recordPunch': 'Record punch',
  'member.punchType': 'Type',
  'member.when': 'Date and time',
  'member.reason': 'Reason',
  'member.punchReasonPlaceholder': 'E.g. works without a smartphone, confirmed by the shift lead',
  'member.punchRecorded': 'Punch recorded.',
  'member.recordLeave': 'Record absence',
  'member.leaveType': 'Absence type',
  'member.from': 'From',
  'member.to': 'To',
  'member.quantityDays': 'Days',
  'member.quantityHours': 'Hours',
  'member.protocol': 'INPS certificate protocol',
  'member.leaveReasonPlaceholder': 'E.g. certificate received from the doctor, agreed holidays',
  'member.leaveRecorded': 'Absence recorded.',
  'member.sickDurationAuto': 'Duration is calculated automatically in calendar days from the period.',
  'payroll.showDocuments': 'Documents',
  'payroll.hideDocuments': 'Hide',
  'payroll.withdrawnCount': '{n} withdrawn',
  'payroll.withdrawn': 'Withdrawn',
  'payroll.openedOn': 'Opened {when}',
  'payroll.notOpened': 'Not opened yet',
  'payroll.withdraw': 'Withdraw',
  'payroll.withdrawTitle': 'Withdraw {name}’s document',
  'payroll.withdrawExplain': '“{title}” disappears from the employee’s app and the key that opens it is destroyed: nobody will be able to decrypt it. Use it if it went to the wrong person or by mistake.',
  'payroll.withdrawAlreadyOpened': 'The recipient has already opened it.',
  'payroll.withdrawPlaceholder': 'E.g. sent to the wrong person',
  'payroll.withdrawConfirm': 'Withdraw the document',
  'payroll.withdrawDone': 'Document withdrawn.',
  'payroll.withdrawBreach': 'The recipient had already downloaded it and may have read it. If it was the wrong person, this is a personal data breach: the company must assess it and, if there is a risk to the person concerned, notify the Garante within 72 hours (art. 33 GDPR). Record it in the breach register.',
  'payroll.withdrawNotDownloaded': 'It had not been downloaded yet: nobody has read it.',
  'payroll.withdrawFileLater': 'The encrypted file was not deleted now; without the key it is unreadable anyway.',
  'payroll.withdrawResend': 'To send it to the right person, upload the PDF again and assign the page by hand.',
  'privacy.title': 'Privacy requests (GDPR)',
  'privacy.intro': 'Employees (and former employees, for 12 months) file these requests from the app. The law gives one month to answer, extendable once by two months with a reason (art. 12 GDPR). The employee reads your answer in the app.',
  'privacy.empty': 'No requests.',
  'privacy.emptyOpen': 'No requests to handle.',
  'privacy.emptyClosed': 'No requests in history.',
  'privacy.tabOpen': 'To handle',
  'privacy.tabClosed': 'History',
  'privacy.results': 'Requests {from}–{to} of {total}',
  'privacy.pagination': 'Privacy request pages',
  'privacy.countUnavailable': 'Privacy request count unavailable',
  'privacy.received': 'Received',
  'privacy.kind': 'Type',
  'privacy.due': 'Deadline',
  'privacy.overdue': 'Overdue',
  'privacy.answer': 'Answer',
  'privacy.view': 'View',
  'privacy.openCount': '{n} open requests',
  'privacy.kind.access': 'Access',
  'privacy.kind.portability': 'Portability',
  'privacy.kind.erasure': 'Erasure',
  'privacy.kind.rectification': 'Rectification',
  'privacy.kind.objection': 'Objection',
  'privacy.status.pending': 'To handle',
  'privacy.status.in_progress': 'In progress',
  'privacy.status.completed': 'Completed',
  'privacy.status.rejected': 'Rejected',
  'privacy.panelTitle': '{kind} · {name}',
  'privacy.receivedOn': 'Received {when}',
  'privacy.dueOn': 'answer by {when}',
  'privacy.extended': 'extended',
  'privacy.extensionNote': 'Reason for the extension: {note}',
  'privacy.guide.access': 'The employee can download all the data Fide holds by themselves (app → Privacy → Export). In your answer confirm this and add what the company keeps outside Fide (paper file, payroll software).',
  'privacy.guide.portability': 'The export from the app (JSON) is already in a portable format. Confirm it in your answer and point out any data in other systems.',
  'privacy.guide.erasure': 'Attendance, payslips and employment documents must be kept by law (art. 17.3.b GDPR; Libro Unico del Lavoro, 5 years): usually you answer explaining this. For a former employee you can erase at once what is not mandatory (phone keys, notifications).',
  'privacy.guide.rectification': 'Correct the data on the Employees page (Manage → Details) and confirm in your answer what you changed.',
  'privacy.guide.objection': 'Check whether the processing relies on legitimate interest: if so it must stop unless there are overriding grounds, which you explain in the answer (art. 21 GDPR).',
  'privacy.answerLabel': 'Answer to the employee',
  'privacy.answerHint': 'It needs a clear answer: what you did or why it is not possible, and the right to complain to the Garante.',
  'privacy.useTemplate': 'Use the mandatory-retention text',
  'privacy.template.erasureRetention': 'We have received your erasure request. Punches, payslips and other employment documents must be kept by law (art. 17(3)(b) GDPR; Libro Unico del Lavoro, 5 years) and cannot be erased now: they will be when the retention periods end. Other data that was not needed has been removed. You can lodge a complaint with the Garante per la protezione dei dati personali.',
  'privacy.takeOn': 'Mark in progress',
  'privacy.reject': 'Reject',
  'privacy.complete': 'Mark as completed',
  'privacy.eraseFormerExplain': 'Former employee: you can erase at once what does not have to be kept. Their phone keys are revoked (their documents in Fide can no longer be opened) and notifications are deleted. The request closes with the answer written above.',
  'privacy.eraseFormer': 'Erase non-mandatory data and close',
  'privacy.extendExplain': 'Complex request? You can extend by two months, once, within the first month. The employee will see the reason.',
  'privacy.extendPlaceholder': 'Reason for the extension',
  'privacy.extend': 'Extend by 2 months',

  // Requests: history and decision notes
  'requests.tabPending': 'To handle ({n})',
  'requests.tabHistory': 'History',
  'requests.approveTitle': 'Approve {name}’s request',
  'requests.rejectTitle': 'Reject {name}’s request',
  'requests.noteRequired': 'Reason for rejecting',
  'requests.batchApprove': 'Approve selected ({n})',
  'requests.batchReject': 'Reject selected ({n})',
  'requests.batchLimit': 'Up to 50 ordinary leave requests at a time.',
  'requests.selectPage': 'Select all ordinary requests on this page',
  'requests.selectRequest': 'Select {name}’s request',
  'requests.batchApproveTitle': 'Approve {n} requests',
  'requests.batchRejectTitle': 'Reject {n} requests',
  'requests.batchAtomic': 'This is all-or-nothing: every request is revalidated by the server and no request changes if one fails.',
  'requests.batchConfirm': 'I have reviewed every selected request and confirm this decision.',
  'requests.noteOptional': 'Note (optional)',
  'requests.noteHint': 'The employee sees it in the app.',
  'requests.filterAllKinds': 'All types',
  'requests.filterAllOutcomes': 'All outcomes',
  'requests.outcome': 'Outcome',
  'requests.status.approved': 'Approved',
  'requests.status.rejected': 'Rejected',
  'requests.status.cancelled': 'Cancelled',
  'requests.searchPerson': 'Search by name',
  'requests.historyEmpty': 'No requests in the history.',
  'requests.decidedBy': 'Decided by',
  'requests.byHr': 'Recorded by HR',

  // Roles: team managers in the portal
  'member.role': 'Role',
  'member.roleSave': 'Change role',
  'member.roleHint': 'The owner appoints HR; HR appoints team managers.',
  'member.roleManagerHint': 'Set the team in each employee’s “Manager” field. Appointing a manager does not automatically assign the whole company to them.',
  'access.managerScope': 'You see your team only.',

  // Payslips: name cross-check
  'payroll.nameMissing': 'Employee name not found on pages {pages}: check they are theirs.',
  'payroll.reviewTitle': 'Check before sending',
  'payroll.reviewBody': 'Open each page link and check that it belongs to the selected worker. Even when the codice fiscale matches, visually check the document. The confirmation box unlocks after opening the link; no page is encrypted or published without confirmation.',
  'payroll.reviewOpenFirst': 'Open the PDF page before confirming its assignment.',
  'payroll.reviewOpenPage': 'Open PDF page {page}',
  'payroll.reviewPageConfirm': 'I confirm page {page} belongs to {name}.',
  'payroll.reviewGroup': '{name} · pages {pages}',
  'payroll.reviewPageReference': 'Assignment: {match} · detected CF: {cf}',
  'payroll.match.cf': 'matching codice fiscale',
  'payroll.match.continuation': 'continuation of previous page',
  'payroll.match.manual': 'manual selection',
  'payroll.issue.duplicate_employee': 'The codice fiscale on page {page} belongs to more than one employee. Review the page and select the correct recipient.',
  "member.tab.balances": "Leave balances",
  "balances.reload": "Reload balances before retrying",
  "balances.year": "Year",
  "balances.type": "Leave type",
  "balances.days": "days",
  "balances.hours": "hours",
  "balances.credited": "Accrued / credited this year",
  "balances.carried": "Previous-year carry-over",
  "balances.sourceKind": "Balance source",
  "balances.sourceConsultant": "Labour consultant",
  "balances.sourcePayslip": "Reviewed payslips",
  "balances.sourceOther": "Other reviewed source",
  "balances.asOfDate": "Balance as of",
  "balances.sourceReference": "Reference / period (optional)",
  "balances.referenceHint": "Do not include diagnoses or health data; use a period or payroll reference only.",
  "balances.summary": "Total used: {used}; pending: {pending}; available: {remaining} {unit}.",
  "balances.updated": "Updated: {at}",
  "balances.saved": "Balance saved.",
  "balances.missing": "Balance not entered: the employee sees “not entered”, not zero.",
  "balances.noTypes": "No active leave types track a balance.",
  "balances.explain": "Enter gross credited amounts, carry-over and leave used outside Fide, verified with payroll. Fide also deducts approved requests starting in the selected year: do not enter net remaining amounts or count those requests as external usage. Split requests crossing year-end by year. HR updates accrual; no automatic calculation by collective agreement.",
  "balances.usedExternal": "Used outside Fide (do not duplicate requests already in Fide)",
  "balances.movements": "Fide movements after the balance date: {n} {unit} (included in available).",
  "balances.ledgerOnly": "No balance declared: only movements recorded in Fide. State the source and date of the balance before saving it.",
  "monthClose.title": "Monthly close",
  "monthClose.explain": "Close a month once reviewed: nothing may be pending. Once closed nobody can change it (requests, decisions, forgotten punches, overtime); punches signed by phones are always accepted. Reopening needs a reason, kept in the history.",
  "monthClose.month": "Month",
  "monthClose.state": "Status",
  "monthClose.open": "Open",
  "monthClose.closed": "Closed",
  "monthClose.reopened": "Reopened",
  "monthClose.close": "Close",
  "monthClose.reopen": "Reopen",
  "monthClose.reason": "Reason for reopening",
  "monthClose.confirmReopen": "Confirm reopening",
  "monthClose.history": "Close history",
  "monthClose.errClosed": "The month is closed: HR must reopen it to change it.",
  "monthClose.errPending": "There are pending requests in this month: decide them before closing.",
  "monthClose.errNotOver": "Only a month that has ended can be closed.",
  "monthClose.errReason": "Write a reason of at least 10 characters.",
  "restricted.title": "Unavailable periods",
  "restricted.explain": "Dates when employees cannot request absences (e.g. a peak or the inventory), for the whole company or one site, for all types or one. Employees can see them. HR can still record an absence on someone's behalf. Sick leave and Law 104 can never be blocked.",
  "restricted.empty": "No periods.",
  "restricted.label": "Reason shown to employees",
  "restricted.from": "From",
  "restricted.to": "To",
  "restricted.site": "Site",
  "restricted.allSites": "All sites",
  "restricted.type": "Type",
  "restricted.allTypes": "All types (except sick leave and 104)",
  "restricted.add": "Add period",
  "restricted.delete": "Delete",
  "restricted.errProtected": "Sick leave and Law 104 cannot be blocked.",
  "restricted.errInvalid": "Check the dates (at most one year) and the reason.",
  "restricted.errBlocked": "The dates fall in a period closed to requests.",
  "balanceImport.button": "Import balances",
  "balanceImport.title": "Import balances from the payroll consultant",
  "balanceImport.body": "Upload the balances file (CSV or Excel saved as CSV): tax code, type (FERIE, ROL…), year, accrued, carry-over, used outside Fide, reference date and source. Fide checks every row and shows before/after; all or nothing is applied. Fide movements after the reference date keep adding up.",
  "balanceImport.summary": "{ready} rows ready, {errors} with errors, {negative} with negative availability.",
  "balanceImport.person": "Person",
  "balanceImport.type": "Type and year",
  "balanceImport.before": "Now (accrued + carry-over − used outside)",
  "balanceImport.after": "From the file",
  "balanceImport.available": "Available after",
  "balanceImport.none": "No balance",
  "balanceImport.reconcileHint": "Availability subtracts requests approved in Fide in the year. A negative value is a difference to check with the consultant before importing.",
  "balanceImport.apply": "Import {n} rows",
  "balanceImport.done": "{n} balances imported.",
  "balanceImport.rowFailed": "Row {line}: {reason}. No balance was changed.",
  "balanceImport.missing": "Missing required columns: {columns}.",
  "balanceImport.tooMany": "At most {max} rows per file.",
  "balanceImport.col.codiceFiscale": "tax code",
  "balanceImport.col.type": "type",
  "balanceImport.col.year": "year",
  "balanceImport.col.entitled": "accrued",
  "balanceImport.col.carried": "carry-over",
  "balanceImport.col.external": "used outside",
  "balanceImport.col.asOf": "reference date",
  "balanceImport.col.source": "source",
  "balanceImport.col.note": "reference",
  "balanceImport.issue.cf": "missing tax code",
  "balanceImport.issue.person": "no employee with this tax code",
  "balanceImport.issue.type": "unknown type or no balance",
  "balanceImport.issue.year": "invalid year",
  "balanceImport.issue.number": "invalid value",
  "balanceImport.issue.date": "invalid date or outside the year",
  "balanceImport.issue.futureDate": "future date",
  "balanceImport.issue.source": "source: consulente, cedolino or altro",
  "balanceImport.issue.note": "reference too long",
  "balanceImport.issue.duplicate": "repeated row",
  "balanceImport.issue.value": "invalid value",
  "balanceImport.issue.other": "unexpected error",
  "nav.calendar": "Absence calendar",
  "calendar.title": "Absence calendar",
  "calendar.explainHr": "Approved and pending absences for the whole company or one site, with day-by-day coverage. Rejected and cancelled ones are not shown.",
  "calendar.explainManager": "Approved and pending absences of your team, with day-by-day coverage. For sick leave and Law 104 you only see “Absent”, without the reason.",
  "calendar.previous": "Previous month",
  "calendar.next": "Next month",
  "calendar.site": "Site",
  "calendar.allSites": "All sites",
  "calendar.legendLeave": "Approved absence",
  "calendar.legendPending": "Pending",
  "calendar.legendRemote": "Smart working",
  "calendar.empty": "No people to show.",
  "calendar.person": "Person",
  "calendar.absent": "Absent",
  "calendar.private": "Absent",
  "calendar.pending": "pending",
  "delegations.title": "Approval delegations",
  "delegations.explain": "While you are away you can hand your approvals to another manager or HR, for up to 90 days. It expires on its own, does not pass on to others, and nobody can approve their own requests.",
  "delegations.empty": "No delegations.",
  "delegations.from": "Delegating",
  "delegations.to": "Delegate",
  "delegations.period": "Period",
  "delegations.state": "Status",
  "delegations.upcoming": "Scheduled",
  "delegations.active": "Active",
  "delegations.ended": "Ended",
  "delegations.end": "End",
  "delegations.start": "From",
  "delegations.finish": "To",
  "delegations.create": "Create delegation",
  "delegations.errPerson": "The delegate must be an active manager or HR user, other than the person delegating.",
  "delegations.errDates": "Check the dates: at most 90 days and not in the past.",
  "delegations.errOverlap": "This person already has a delegation on those dates.",
  "leaveTypes.title": "Absence types",
  "leaveTypes.explain": "Names and rules of the types employees can request. A type with a balance always needs a person's decision. System types keep their code, unit and rules: you can rename them and, where allowed, deactivate them.",
  "leaveTypes.name": "Name",
  "leaveTypes.unit": "Unit",
  "leaveTypes.rules": "Rules",
  "leaveTypes.status": "Status",
  "leaveTypes.system": "system",
  "leaveTypes.protocol": "INPS protocol",
  "leaveTypes.approval": "Approval",
  "leaveTypes.noApproval": "No approval",
  "leaveTypes.balance": "with balance",
  "leaveTypes.active": "Active",
  "leaveTypes.inactive": "Inactive",
  "leaveTypes.edit": "Edit",
  "leaveTypes.editTitle": "Edit “{name}”",
  "leaveTypes.newTitle": "New absence type",
  "leaveTypes.new": "Add type",
  "leaveTypes.systemHint": "Managed by Fide: you can only change the name (and the status, where allowed).",
  "leaveTypes.code": "Code",
  "leaveTypes.tracksBalance": "Has a balance (holidays, leave…)",
  "leaveTypes.requiresApproval": "Requires approval",
  "leaveTypes.noApprovalWarning": "Without approval, requests are approved immediately. Use it only for notices that need no decision.",
  "leaveTypes.errNeedsApproval": "A type with a balance always requires a person's approval.",
  "leaveTypes.errReserved": "This code is reserved for a system type.",
  "leaveTypes.errExists": "A type with this code already exists.",
  "leaveTypes.errSystem": "For system types you can only change the name and the status.",
  "leaveTypes.errRequired": "Malattia and Banca Ore must stay active.",
  "leaveTypes.errRetired": "Overtime is handled by overtime requests: this type stays inactive.",
  "leaveTypes.errStale": "Someone else changed this type. Reload and try again.",
  "leaveTypes.errInUse": "You cannot change the unit: the type is already used in requests or balances.",
  "leaveTypes.grantedOnly": "enabled people only",
  "leaveTypes.eligibility": "Who can request it",
  "leaveTypes.eligibilityEveryone": "All active employees",
  "leaveTypes.eligibilityGranted": "Only people enabled by HR",
  "leaveTypes.errEligibilityFixed": "Malattia and Ferie are always for everyone; Law 104 and Smart Working always only for enabled people.",
  "leaveTypes.errNotEligible": "This person is not enabled for this absence type on the chosen dates.",
  "grants.title": "Enablements",
  "grants.explain": "Who can request restricted types (Law 104, Smart Working…) and when. Ending an enablement keeps history and pending requests.",
  "grants.empty": "No enablements.",
  "grants.type": "Type",
  "grants.from": "From",
  "grants.to": "To",
  "grants.toOptional": "To (optional)",
  "grants.open": "No end date",
  "grants.end": "End",
  "grants.confirmEnd": "Confirm end",
  "grants.add": "Enable",
  "grants.self": "Your own enablements are managed by another HR user or the owner.",
  "grants.privacy": "Person, type and dates only: do not enter reasons, relatives, diagnoses or documents. Only HR, the owner and the person can see it.",
  "grants.errOverlap": "There is already an enablement for these dates.",
  "grants.errDates": "Check the dates: the end cannot be before the start.",
  "ledger.title": "Movements recorded in Fide",
  "ledger.explain": "Time bank credits, adjustments and reversals. They cannot be edited: a mistake is corrected with a reversal. Movements up to the “Balance as of” date are already included in the source balance and are not added again.",
  "ledger.empty": "No movements for this year.",
  "ledger.date": "Date",
  "ledger.kind": "Type",
  "ledger.quantity": "Quantity",
  "ledger.reason": "Reason",
  "ledger.kindBancaOre": "Overtime to time bank",
  "ledger.kindAdjustment": "HR adjustment",
  "ledger.kindReversal": "Reversal",
  "ledger.included": "Already included in the source balance",
  "ledger.reverse": "Reverse",
  "ledger.reverseReason": "Reason for reversal",
  "ledger.confirmReverse": "Confirm reversal",
  "ledger.adjustTitle": "New adjustment",
  "ledger.quantitySigned": "Quantity ({unit}, negative to subtract)",
  "ledger.adjust": "Record adjustment",
  "ledger.notReversible": "This movement cannot be reversed (already reversed or itself a reversal).",
  "presence.date": "Date (Italy)",
  "presence.resetToday": "Today",
  "presence.search": "Name, employee number or receipt",
  "presence.siteFilter": "Punch site",
  "presence.managerFilter": "Assigned supervisor",
  "presence.kindFilter": "Type",
  "presence.all": "All",
  "presence.review": "Technical signals",
  "presence.scope": "Filtered results: {filtered} / {total} punches for the day. Signals are not requests awaiting approval; approved corrections remain visible in the table.",
  "presence.lastIn": "Employees in results",
  "presence.lastInHint": "People with at least one punch in the filtered results; this is not a count of people currently present.",
  "presence.dayPunches": "Punches for the day",
  "presence.noMatches": "No results with these filters.",
  "presence.pagination": "Punch pages",
  "presence.previous": "Previous",
  "presence.next": "Next",
  "presence.page": "Page {page} of {pages}",
  "requests.searchEmployee": "Name or employee number",
  "requests.assignedSite": "Assigned site",
  "requests.from": "Requested period: from",
  "requests.to": "Requested period: to",
  "requests.resetFilters": "Clear filters",
  "requests.filterScope": "Site and supervisor reflect current assignments. Dates filter the requested absence period or punch, including overlapping periods; not the decision date.",
  "requests.invalidRange": "The start date must be on or before the end date.",
  "requests.pagination": "Request pages",
  "requests.results": "Filtered: {filtered} / {total} requests",
  'allocation.title': 'Daily allocation',
  'allocation.hint': 'Allocate the existing approved total without changing it. Dates are not inferred from the current calendar.',
  'allocation.add': 'Add day',
  'allocation.total': 'Allocated total',
  'allocation.confirm': 'I confirm the dates and approved total',
  'allocation.history': 'Previous versions',
  'allocation.invalid': 'Invalid dates, quantities or total. The approved total must remain unchanged.',
  'allocation.stale': 'The allocation changed. Reload the saved version before retrying.',
  "budget.policy": "Calendar-based absence calculation",
  "budget.policyHint": "Applies calendar calculation to full working days and hourly leave based on scheduled shifts. Each scheduled working day is attributed to the shift start date without double counting after midnight. Requires verified schedules and calendars. Sickness keeps its current route. Does not change earlier request totals or accrue leave. Update and verify the apps before enabling: older versions cannot submit these requests.",
  "budget.enabled": "Enabled",
  "budget.disabled": "Disabled",
  "budget.enable": "Enable calendar calculation",
  "budget.disable": "Disable for new requests",
  "budget.preview": "{count} days calculated by the server",
  "budget.fullDays": "Calculation follows the shift and calendar. Hourly leave can cover part of the working day.",
  "budget.stale": "Configuration changed. Recalculate before submitting. For a pending request: cancel it and create it again with the new calculation.",
  "budget.scheduleRequired": "A compatible schedule is missing within the period. Ask HR to complete it.",
  "budget.noWorkdays": "No working days are scheduled within this period.",
  "budget.error": "The request could not be calculated. Check the details and retry.",
  "budget.noSavedAllocation": "This request has no saved daily allocation. Its total is not reconstructed from the current calendar.",
  'member.tab.onboarding': 'Onboarding checklist',
  'member.tab.deadlines': 'Deadlines',
  'onboarding.checklist.title': 'Onboarding procedures and documents',
  'onboarding.checklist.hint': 'Check off the forms and documents handed in by the employee for hiring. The employee sees the updated status in their app (My data).',
  'onboarding.checklist.task.identity_document': 'Identity document and fiscal code',
  'onboarding.checklist.task.privacy_notice': 'Privacy policy notice (GDPR art. 13)',
  'onboarding.checklist.task.tfr_choice': 'Severance pay (TFR) destination choice form',
  'onboarding.checklist.task.tax_deductions': 'Tax deductions / dependents declaration form',
  'onboarding.checklist.task.bank_details': 'Bank account details (IBAN for salary deposit)',
  'onboarding.checklist.task.employment_contract': 'Contract linked to the employment version (does not prove signature)',
  'onboarding.checklist.task.individual_agreement': 'Expected individual agreement (does not prove signature)',
  'onboarding.checklist.status.completed': 'Completed',
  'onboarding.checklist.status.pending': 'Pending',
  'onboarding.checklist.completedAt': 'Completed on {date}',
  'onboarding.checklist.completedBy': 'by {actor}',
  'onboarding.checklist.notes': 'Internal HR notes',
  'onboarding.checklist.save': 'Save changes',
  'onboarding.checklist.saved': 'Changes saved successfully.',
  'deadlines.title': 'Compliance and deadlines tracker',
  'deadlines.hint': 'Monitoring contractual and statutory obligations (probation period, contract expiration, medical fitness visit, residence permit, safety training).',
  'deadlines.horizon': 'Time horizon',
  'deadlines.horizon.30': 'Next 30 days',
  'deadlines.horizon.60': 'Next 60 days',
  'deadlines.horizon.90': 'Next 90 days',
  'deadlines.horizon.365': 'Full year',
  'deadlines.category.probation_end': 'Probation end',
  'deadlines.category.contract_end': 'Contract expiry',
  'deadlines.category.medical_visit': 'Periodic medical visit (D.Lgs. 81/08)',
  'deadlines.category.residence_permit': 'Residence permit',
  'deadlines.category.safety_training': 'Safety training',
  'deadlines.urgency.expired': 'Expired',
  'deadlines.urgency.critical': 'Critical (<= 15 d)',
  'deadlines.urgency.warning': 'Expiring soon (<= 30 d)',
  'deadlines.urgency.upcoming': 'Scheduled',
  'deadlines.daysRemaining': '{n} d remaining',
  'deadlines.daysExpired': 'Expired {n} d ago',
  'deadlines.empty': 'No deadlines in the selected period.',

  'breaks.title': 'Break policies',
  'breaks.description': 'Company rules for lunch and coffee breaks. No automatic deductions.',
  'breaks.newPolicy': 'New break policy',
  'breaks.editPolicy': 'Edit break policy',
  'breaks.name': 'Name',
  'breaks.nameRequired': 'Name must be at least 2 characters',
  'breaks.type': 'Type',
  'breaks.isPaid': 'Paid break (counts as work time)',
  'breaks.isMandatory': 'Mandatory',
  'breaks.minDuration': 'Minimum duration',
  'breaks.expectedDuration': 'Expected duration',
  'breaks.validFrom': 'Valid from',
  'breaks.validTo': 'Valid to',
  'breaks.minutes': 'minutes',
  'breaks.paid': 'Paid',
  'breaks.unpaid': 'Unpaid',
  'breaks.mandatoryBadge': 'Mandatory',
  'breaks.activeIndefinitely': 'active indefinitely',
  'breaks.durations': 'Expected / min duration',
  'breaks.validity': 'Validity',
  'breaks.empty': 'No break policies configured.',
  'breaks.invalidDuration': 'Duration cannot be negative',
  'breaks.invalidDates': 'End date must be after start date',

  'presence.liveTab': 'Real-time attendance',
  'presence.exportTab': 'Attendance export',
  'presence.breakStart': 'Break start',
  'presence.breakEnd': 'Break end',

  'cartellino.tab': 'Monthly timesheet',
  'cartellino.title': 'Monthly timesheet',
  'cartellino.subtitle': 'Monthly summary of attendance, breaks, hours worked, and anomalies per employee.',
  'cartellino.downloadPdf': 'Download PDF',
  'cartellino.pdfTitle': 'Monthly attendance timesheet',
  'cartellino.period': 'Month of competence',
  'cartellino.vatFiscalCode': 'VAT / Fiscal Code',
  'cartellino.summary': 'Monthly summary',
  'cartellino.employerSignature': 'Employer / HR signature',
  'cartellino.employeeSignature': 'Employee signature for acknowledgment and compliance',
  'cartellino.generatedAt': 'Generated on',
  'cartellino.footerNotice': 'Document generated by Fide based on unalterable electronic records.',
  'cartellino.employee': 'Employee',
  'cartellino.prevMonth': 'Previous month',
  'cartellino.nextMonth': 'Next month',
  'cartellino.workedDays': 'Worked days',
  'cartellino.totalDaysInMonth': '{count} days in month',
  'cartellino.computableHours': 'Computable hours',
  'cartellino.expected': 'Expected',
  'cartellino.noSchedule': 'No schedule',
  'cartellino.breaksSummary': 'Total breaks',
  'cartellino.paid': 'Paid',
  'cartellino.unpaid': 'Unpaid',
  'cartellino.paidShort': 'paid',
  'cartellino.anomalies': 'Anomalies',
  'cartellino.anomaliesHint': 'Events requiring review or correction',
  'cartellino.date': 'Day',
  'cartellino.punches': 'Punches',
  'cartellino.breaks': 'Breaks',
  'cartellino.computable': 'Computable',
  'cartellino.leaves': 'Absences',
  'cartellino.status': 'Status',
  'cartellino.regular': 'Regular',
  'cartellino.missingOut': 'Missing exit',
  'cartellino.missingIn': 'Missing entry',
  'cartellino.unclosedBreak': 'Unclosed break',
  'cartellino.breakWithoutStart': 'Break end without start',
  'cartellino.breakOutsideShift': 'Break outside shift',
  'cartellino.shortBreak': 'Break below minimum',
  'cartellino.longShift': 'Shift over 16h',
  'cartellino.corrected': 'Manual correction',
  'cartellino.flagged': 'Flagged punch',
  'cartellino.scheduleDiscrepancy': 'Unexcused absence',
  'common.days.mon': 'Mon',
  'common.days.tue': 'Tue',
  'common.days.wed': 'Wed',
  'common.days.thu': 'Thu',
  'common.days.fri': 'Fri',
  'common.days.sat': 'Sat',
  'common.days.sun': 'Sun',
  'compliance.status': "Administrative status",
  'compliance.scheduled': "Scheduled",
  'compliance.document_pending': "Document pending",
  'compliance.verified': "Verified",
  'breaks.versionRequired': "Create a version effective after the breaks already recorded.",
  'error.bankConfiguration': "The time bank must be active, measured in hours and track its balance.",
  "schedule.copyDay": "Copy a working day",
  "schedule.copyHint": "Copy every slot from the selected day, including the breaks between slots. This replaces slots only for the selected days in the editor: it does not save or change punches. Then check the contractual total and save the new version.",
  "schedule.copySource": "Source day",
  "schedule.copyTargets": "Days to replace",
  "schedule.copyInvalid": "Choose a day with valid slots. The copy must stay within 28 slots and avoid overlaps, including Sunday night into Monday.",
  "schedule.copySummary": "Existing slots replaced: {count}. New weekly total: {minutes} minutes.",
  "schedule.copyConfirm": "I have checked the days, times and slots being replaced.",
  "schedule.copyApply": "Apply copy in the editor",
  "clockPolicy.methodsTitle": "Clocking methods by scope and date",
  "clockPolicy.methodsHint": "QR always remains available. A more specific active version overrides team, site and company versions. Changes start no earlier than tomorrow and are enforced by the server.",
  "clockPolicy.company": "Company",
  "clockPolicy.site": "Site",
  "clockPolicy.team": "Team",
  "clockPolicy.person": "Person",
  "clockPolicy.scope": "Scope",
  "clockPolicy.target": "Target",
  "clockPolicy.from": "Effective from",
  "clockPolicy.to": "Effective to (optional)",
  "clockPolicy.allowGeo": "Also allow on-device geofence clocking",
  "clockPolicy.geoLegal": "Enable only after the required labour/privacy review. The site must separately have geofence enabled; no coordinates are stored.",
  "clockPolicy.source": "Decision or agreement reference",
  "clockPolicy.confirmMethods": "I have checked the scope, dates, method and supporting reference.",
  "clockPolicy.saveVersion": "Save method version",
  "clockPolicy.qrOnly": "QR only",
  "clockPolicy.qrGeo": "QR + geofence",
  "shifts.title": "Planned shifts",
  "shifts.explain": "Plans are separate from actual punches. Every change creates a new version and an unread notice for the employee. Overlaps and approved absences are blocked.",
  "shifts.time": "Planned time",
  "shifts.notice": "Employee notice",
  "shifts.read": "Read",
  "shifts.unread": "Unread",
  "shifts.cancelled": "Cancelled",
  "shifts.cancel": "Cancel shift",
  "shifts.cancelTitle": "Cancel {name}’s shift",
  "shifts.empty": "No shifts are planned in this period.",
  "shifts.add": "Plan a shift",
  "shifts.edit": "Change planned shift",
  "shifts.start": "Starts",
  "shifts.end": "Ends",
  "shifts.reason": "Reason for this version",
  "shifts.note": "Note for the employee (optional)",
  "shifts.confirm": "I checked the person, site, times, coverage and conflicts.",
  "approvalPolicies.title": "Approval levels",
  "approvalPolicies.explain": "Configure the path for new requests. Each request keeps the levels active when it was submitted; later changes do not rewrite decisions already in progress.",
  "approvalPolicies.empty": "There are no configurable leave types.",
  "approvalPolicies.leaveType": "Leave type",
  "approvalPolicies.flow": "Path",
  "approvalPolicies.flowFor": "Approval path for {name}",
  "approvalPolicies.legacy": "Manager or HR (one level)",
  "approvalPolicies.managerThenHr": "Manager, then HR",
  "approvalPolicies.hrOnly": "HR only",
  "member.recordOvertime": "Record overtime",
  "member.overtimeRecorded": "Overtime recorded.",
  "mealVouchers.title": "Meal vouchers / Buoni pasto (daily accrual)",
  "mealVouchers.explain": "Configure when an employee earns a meal voucher for the day. Fide shows the count on the timesheet (portal and app) and includes it in the monthly export for the payroll consultant.",
  "mealVouchers.enabled": "Enable meal voucher calculation",
  "mealVouchers.minMinutes": "Minimum worked minutes in the day (e.g. 360 = 6 hours)",
  "mealVouchers.includePaidBreaks": "Count paid breaks toward the daily threshold",
  "mealVouchers.includeSmartWorking": "Include approved Smart Working days",
  "mealVouchers.save": "Save meal voucher rules",
  "mealVouchers.saved": "Meal voucher rules saved.",
  "mealVouchers.col": "Meal vouchers",
  "paghe.title": "Payroll causal codes for the consultant (tracciato paghe)",
  "paghe.explain": "Map Fide items (ordinary hours, absences, overtime, meal vouchers) to the causal code used by the payroll software (Zucchetti, TeamSystem, Inaz…). Leave blank to use Fide’s standard code.",
  "paghe.fideCode": "Fide item",
  "paghe.consultantCode": "Payroll software code",
  "paghe.save": "Save code",
  "paghe.saved": "Code saved.",
  "paghe.item.ORD": "Ordinary worked hours",
  "paghe.item.STRAORD_LIQ": "Overtime for payroll payout",
  "paghe.item.STRAORD_BANCA": "Overtime to time bank",
  "paghe.item.BUONO_PASTO": "Earned meal voucher",
  "report.pagheCsv": "Download movements with payroll codes (CSV)",
  "report.variationsCsv": "Download monthly variations (CSV)",
  "report.variationsSummary": "{n} variations recorded in the month (hires/contracts, terminations, overtime, balance adjustments, meal vouchers).",
  "report.var.col.kind": "Variation type",
  "report.var.col.code": "Payroll code",
  "report.var.col.date": "Date / Period",
  "report.var.col.value": "Value / Detail",
  "report.var.kind.hire_or_contract": "Contract / Classification",
  "report.var.kind.termination": "Termination",
  "report.var.kind.overtime": "Approved overtime",
  "report.var.kind.balance_adjustment": "Balance adjustment",
  "report.var.kind.meal_vouchers": "Monthly meal vouchers",
  "communications.category": "Communication category",
  "communications.cat.notice": "Notice",
  "communications.cat.circular": "Company circular",
  "communications.cat.regulation": "Company policy / regulation",
  "communications.cat.privacy_notice": "Privacy notice (GDPR art. 13)",
  "communications.cat.disciplinary_code": "Disciplinary code (art. 7 L. 300/1970)",
  "communications.requiresAck": "Require explicit receipt confirmation (presa visione) in the app",
  "communications.requiresAckHint": "Passive opens are never tracked: only the worker’s explicit confirmation is recorded.",
  "communications.ackCount": "Confirmed receipts: {n}",
  "communications.ackShow": "Show confirmations",
  "communications.ackHide": "Hide confirmations",
  "communications.ackEmpty": "No confirmations recorded yet.",
  "hrDesk.title": "HR Desk & personal data updates",
  "hrDesk.intro": "Requests submitted by employees from the app: IBAN change, address change, family dependents / tax credits, certificate of employment, and TFR advance.",
  "hrDesk.empty": "No HR desk requests.",
  "hrDesk.cat.iban_change": "IBAN change",
  "hrDesk.cat.address_change": "Address change",
  "hrDesk.cat.family_dependents": "Family dependents / tax credits",
  "hrDesk.cat.service_certificate": "Certificate of employment",
  "hrDesk.cat.tfr_advance": "TFR advance",
  "hrDesk.cat.other": "Other HR request",
  "hrDesk.status.pending": "Pending",
  "hrDesk.status.completed": "Completed",
  "hrDesk.status.rejected": "Rejected",
  "hrDesk.status.cancelled": "Cancelled",
  "hrDesk.manage": "Manage",
  "hrDesk.complete": "Mark as completed",
  "hrDesk.reject": "Reject",
  "hrDesk.noteLabel": "Reply or note for the employee",
  "hrDesk.noteRequired": "Reason required when rejecting (min 3 characters)",
};

